import { ServiceBase } from "services/base-service";
import { getDefinedUrlParams } from "@libs/utils";
import type { IPageableList, IPageableRequest } from "types";
import type { Report, ReportSummary } from "../types";

class StatisticsService extends ServiceBase {
  constructor() {
    super();

    this.baseUrl = `${this.baseUrl}/StatisticReports`;

    this.getReports = this.getReports.bind(this);
    this.deleteReport = this.deleteReport.bind(this);
  }

  async getReports(
    request: IPageableRequest,
    abortController?: AbortController,
  ): Promise<IPageableList<ReportSummary>> {
    const params = {
      page: request.page,
      pageSize: request.pageSize,
      orderBy: request.orderBy,
      descendingOrder: request.descendingOrder,
    };

    const url = `?${getDefinedUrlParams(params)}`;
    const response = await this.sendGet(url, abortController);

    // Mark latest report as undeletable
    if (response?.list?.length) {
      const listCopy = [...response.list];
      listCopy.sort((a: any, b: any) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime());
      const latestReport = listCopy[0];

      if (latestReport) {
        latestReport.isDeletable = false;
      }
    }

    return response;
  }

  async getReport(name: string, abortController?: AbortController): Promise<Report> {
    const url = `/${name}`;
    const response = await this.sendGet(url, abortController);

    if (response?.content) {
      response.content = JSON.parse(response.content);
    }

    return response;
  }

  async deleteReport(id: string, abortController?: AbortController): Promise<string> {
    const url = `/${id}`;
    const response = await this.sendDelete(url, abortController);

    return response;
  }

  async generateReport(abortController?: AbortController): Promise<{ id: string; name: string; report: Report }> {
    const url = `/GenerateReport`;
    const response = await this.sendGet(url, abortController);

    if (response?.report) {
      response.report = JSON.parse(response.report);
    }

    return response;
  }

  async submitReport(
    params: { name: string; snapshotVersionId: string },
    abortController?: AbortController,
  ): Promise<any> {
    const url = `/Submit?${getDefinedUrlParams(params)}`;
    const response = await this.sendGet(url, abortController);

    return response;
  }
}

const statisticsService = new StatisticsService();

export { statisticsService };
